/**
 * One derivation of a tenant's money state, for every V2 surface (COL-533).
 *
 * This exists because the previous shape — the shell deriving the journey
 * deposit gate and the overview separately deriving the action card — let a
 * fix land in one and not the other. A review round "fixed" the waived-deposit
 * bug in the overview while the shell kept the old predicate, and nothing
 * caught it. Deriving once and sharing removes that whole class of drift.
 *
 * Every predicate here is the one V1 and the admin side use:
 *
 * - `isPaymentEffectivelyPaid` from `lib/rent`, NOT
 *   `isEffectivelyPaid` from `lib/paymentStatus`. The names are nearly
 *   identical and the behaviour is not: only the rent one honours an admin
 *   attestation (`marked_paid_at` / `marked_paid_by`), so the paymentStatus
 *   version reports a payment an admin marked paid without a proof as unpaid.
 *
 *   KNOWN DIVERGENCE, flagged for a human call. For RENT, V1 uses the
 *   paymentStatus rule (`Dashboard.tsx:2039`) — on this branch that file is
 *   still the narrower proof-only implementation, because main's delegation
 *   refactor (#1326) was never ported. So while the flag is in play a row an
 *   admin marked paid in cash reads as settled here and as overdue in V1.
 *   This is the direction chosen on purpose: the DEPOSIT path in BOTH
 *   dashboards already goes through this rule (via `isDepositFullyPaid`), and
 *   so does the admin side, so V1's rent view is the one surface that
 *   disagrees with the rest of the product — it tells a tenant who has paid
 *   that they owe money. Matching V1 instead would reproduce that. If the
 *   decision goes the other way, change the import here and nowhere else.
 * - `isDepositFullyPaid` for the deposit gate, which treats a zero/waived
 *   expectation as satisfied where `resolveDepositState` reports "due".
 * - `isSepaDebitInFlight` before calling anything overdue — a debit collecting
 *   after its due date is underway, not in arrears.
 */

import { useMemo } from "react";

import { useNowTick } from "@/hooks/useNowTick";
import {
  useTenantPaymentProofs,
  useTenantPayments,
} from "@/hooks/useTenantPayments";
import {
  getExpectedDepositEUR,
  isDepositFullyPaid,
  isPaymentEffectivelyPaid,
  isSepaDebitInFlight,
  resolveDepositState,
  selectEarliestUnpaidRentPayment,
} from "@/lib/rent";
import {
  bookingIdOf,
  paymentMatchesBooking,
  depositRowsForBooking,
  rentRowsForBooking,
} from "@/lib/tenant-payment-scope";
import { isContractSignedForJourney } from "@/lib/tenantJourney";

export type TenantMoneyFacts = {
  /** True while either query is still resolving — callers must not treat this as "nothing owed". */
  loading: boolean;
  /** True if either query failed — likewise not "nothing owed". */
  error: boolean;
  depositSettled: boolean;
  /**
   * What is still OWED on the deposit, in cents — not the contractual figure.
   *
   * A tenant who has paid EUR 500 of a EUR 1,500 deposit owes EUR 1,000;
   * showing the full EUR 1,500 as due asks them for money they already sent.
   */
  depositRemainingCents: number;
  /** A deposit is owed AND the contract has reached a state where asking is fair. */
  depositActionable: boolean;
  nextRent: any | null;
  /** Past due, and not already being collected by SEPA. */
  rentOverdue: boolean;
  /**
   * The next rent row is a SEPA debit already submitted to the bank.
   *
   * Suppressing "overdue" is only half the answer: left at "upcoming" the
   * tenant is told nothing is happening while their money is in transit, and
   * may pay a second time. V1 shows an explicit in-progress state.
   */
  rentInFlight: boolean;
  /**
   * Everything this booking has settled, newest first, for the Paid list.
   *
   * Rent AND deposit. It was rent-only, which made the payments card's own
   * note ("a paid deposit still appears in the Paid list below") false and
   * left its deposit branch unreachable — a tenant whose only settled item
   * was the deposit saw no history at all.
   */
  paidHistoryRows: any[];
  isPaid: (row: any) => boolean;
  /**
   * The booking's own payment rows, and the proof lookup, exactly as the
   * figures above were derived from them.
   *
   * Exposed so the journey resolver can be fed the SAME inputs rather than
   * re-scoping the ledger itself: `isDepositFullyPaid` called twice on
   * identical inputs cannot disagree, whereas two scoping passes can.
   */
  scopedPayments: any[];
  proofFor: (
    paymentId: string
  ) => { status?: string | null } | null | undefined;
};

export function useTenantMoneyFacts(booking: any): TenantMoneyFacts {
  const {
    data: payments = [],
    isLoading: paymentsLoading,
    isError: paymentsError,
  } = useTenantPayments();

  const ids = useMemo(
    () => payments.map((p: any) => String(p.id)).filter(Boolean),
    [payments]
  );
  const {
    data: proofs = {},
    isLoading: proofsLoading,
    isError: proofsError,
  } = useTenantPaymentProofs(ids);

  // Overdue is time-dependent, so re-derive on a tick rather than only when
  // query data changes — otherwise the answer goes stale across midnight.
  const now = useNowTick();

  return useMemo(() => {
    const proofFor = (paymentId: string) => proofs[String(paymentId)] ?? null;
    const isPaid = (row: any) => isPaymentEffectivelyPaid(row, proofFor);

    const loading = paymentsLoading || (ids.length > 0 && proofsLoading);
    const error = paymentsError || proofsError;

    const id = bookingIdOf(booking);
    const scoped = (payments as any[]).filter((p) =>
      paymentMatchesBooking(p, id)
    );

    const depositSettled = isDepositFullyPaid({
      booking,
      payments: scoped,
      proofFor,
    });

    const depositState = resolveDepositState({
      depositPayments: scoped.filter((p: any) => p?.type === "deposit"),
      isEffectivelyPaid: (p: any) => isPaymentEffectivelyPaid(p, proofFor),
      expectedDepositEUR: getExpectedDepositEUR(booking),
    });
    const depositRemainingCents = Math.max(
      0,
      depositState.expectedCents - depositState.paidCents
    );

    /*
      The deposit gate is the JOURNEY's own "contract signed" predicate, not a
      `contract_status` allow-list.

      This read `contract_status in ("approved", "signed")`, which misses every
      signature that does not land in that column: a DocuSign return stamps
      `contract_signed_at` (and leaves `contract_status` on its envelope value),
      and a manually handled tenancy only ever gets `signed_contract_url`. For
      those bookings the gate stayed shut, the overview dropped the deposit
      action, and a tenant with an unpaid deposit read "All done — welcome
      home." while V1, the admin side and this very dashboard's own stage
      ladder all showed Deposit pending.

      `isContractSignedForJourney` is that ladder's gate (JOURNEY_GATES,
      `deposit_pending`), so the card and the stage can no longer disagree.
    */
    const depositActionable =
      !depositSettled && isContractSignedForJourney(booking);

    const rentRows = rentRowsForBooking(payments as any[], id);
    const nextRent = selectEarliestUnpaidRentPayment(
      rentRows,
      isPaid,
      () => false
    );

    const rentInFlight = isSepaDebitInFlight(nextRent);

    const rentOverdue =
      !!nextRent?.due_date &&
      new Date(nextRent.due_date) < new Date(new Date(now).toDateString()) &&
      // A SEPA debit collecting after its due date is in flight, not in
      // arrears — telling the tenant to act would ask for a second payment.
      !rentInFlight;

    /*
      Rent and deposit together. Both are things the tenant has paid, and the
      card below has always claimed to list both; only rent ever arrived.

      Sorted on `due_date` like before. A deposit row carries one too, so the
      two interleave by when they fell due rather than by type.
    */
    const paidHistoryRows = [
      ...rentRows,
      ...depositRowsForBooking(payments as any[], id),
    ]
      .filter(isPaid)
      .sort((a: any, b: any) =>
        String(b.due_date ?? "").localeCompare(String(a.due_date ?? ""))
      );

    return {
      loading,
      error,
      depositSettled,
      depositRemainingCents,
      depositActionable,
      nextRent,
      rentOverdue,
      rentInFlight,
      paidHistoryRows,
      isPaid,
      scopedPayments: scoped,
      proofFor,
    };
  }, [
    payments,
    proofs,
    ids.length,
    paymentsLoading,
    paymentsError,
    proofsLoading,
    proofsError,
    booking,
    now,
  ]);
}
